import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
    Plus, Search, Key, Edit, Trash2, MapPin, 
    Eye, EyeOff, Check, Copy, Server, Wifi, Monitor,
    Printer, Camera, Globe, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const deviceTypes = [
    { value: 'router', label: 'Router', icon: Globe },
    { value: 'switch', label: 'Switch', icon: Server },
    { value: 'server', label: 'Sunucu', icon: Server },
    { value: 'printer', label: 'Yazıcı', icon: Printer },
    { value: 'camera', label: 'Kamera', icon: Camera },
    { value: 'access_point', label: 'Access Point', icon: Wifi },
    { value: 'software', label: 'Yazılım', icon: Monitor },
    { value: 'other', label: 'Diğer', icon: Settings },
];

export function Passwords() {
    const { isAdmin } = useAuth();
    const [passwords, setPasswords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [showPasswords, setShowPasswords] = useState({});

    const [formData, setFormData] = useState({
        device_name: '',
        device_type: 'other',
        username: '',
        password: '',
        ip_address: '',
        url: '',
        location: '',
        notes: '',
    });

    useEffect(() => {
        loadPasswords();
    }, [typeFilter]);

    const loadPasswords = async (searchTerm = '') => {
        try {
            const token = localStorage.getItem('token');
            let url = `${API_URL}/api/passwords`;
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (typeFilter && typeFilter !== 'all') params.append('device_type', typeFilter);
            if (params.toString()) url += `?${params.toString()}`;
            
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setPasswords(data);
            }
        } catch (error) {
            console.error('Load error:', error);
            toast.error('Şifre listesi yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        loadPasswords(value);
    };

    const togglePassword = (id) => {
        setShowPasswords(prev => ({...prev, [id]: !prev[id]}));
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Panoya kopyalandı');
    };

    const openCreateDialog = () => {
        setEditingEntry(null);
        setFormData({
            device_name: '',
            device_type: 'other',
            username: '',
            password: '',
            ip_address: '',
            url: '',
            location: '',
            notes: '',
        });
        setDialogOpen(true);
    };

    const openEditDialog = (entry) => {
        setEditingEntry(entry);
        setFormData({
            device_name: entry.device_name || '',
            device_type: entry.device_type || 'other',
            username: entry.username || '',
            password: entry.password || '',
            ip_address: entry.ip_address || '',
            url: entry.url || '',
            location: entry.location || '',
            notes: entry.notes || '',
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.device_name || !formData.username || !formData.password) {
            toast.error('Cihaz adı, kullanıcı adı ve şifre zorunludur');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const url = editingEntry 
                ? `${API_URL}/api/passwords/${editingEntry.id}`
                : `${API_URL}/api/passwords`;
            
            const res = await fetch(url, {
                method: editingEntry ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (res.ok) {
                toast.success(editingEntry ? 'Şifre güncellendi' : 'Şifre eklendi');
                setDialogOpen(false);
                loadPasswords(search);
            } else {
                toast.error('İşlem başarısız');
            }
        } catch (error) {
            console.error('Submit error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const handleDelete = async (entry) => {
        if (!window.confirm(`"${entry.device_name}" şifresini silmek istediğinize emin misiniz?`)) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/passwords/${entry.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (res.ok) {
                toast.success('Şifre silindi');
                loadPasswords(search);
            } else {
                toast.error('Silme işlemi başarısız');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const getDeviceIcon = (type) => {
        const device = deviceTypes.find(d => d.value === type);
        return device ? device.icon : Settings;
    };

    const getDeviceLabel = (type) => {
        const device = deviceTypes.find(d => d.value === type);
        return device ? device.label : type;
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <div className="grid gap-4">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="passwords-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Şifre Yönetimi</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Toplam {passwords.length} cihaz şifresi kayıtlı
                    </p>
                </div>
                {isAdmin && (
                    <Button 
                        onClick={openCreateDialog}
                        className="bg-orwion-green hover:bg-orwion-green-hover" 
                        data-testid="create-password-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Yeni Şifre
                    </Button>
                )}
            </div>

            {/* Search & Filter */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Cihaz adı, kullanıcı, IP veya konum ile ara..."
                                value={search}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="pl-10"
                                data-testid="search-input"
                            />
                        </div>
                        <Select value={typeFilter} onValueChange={setTypeFilter}>
                            <SelectTrigger className="w-full sm:w-48" data-testid="type-filter">
                                <SelectValue placeholder="Cihaz Tipi" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tüm Cihazlar</SelectItem>
                                {deviceTypes.map(type => (
                                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Passwords List */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-lg shadow">
                    <thead>
                        <tr className="bg-slate-50 border-b">
                            <th className="text-left p-4 font-medium text-slate-600">Cihaz</th>
                            <th className="text-left p-4 font-medium text-slate-600">Tip</th>
                            <th className="text-left p-4 font-medium text-slate-600">Kullanıcı Adı</th>
                            <th className="text-left p-4 font-medium text-slate-600">Şifre</th>
                            <th className="text-left p-4 font-medium text-slate-600">IP / URL</th>
                            <th className="text-left p-4 font-medium text-slate-600">Konum</th>
                            <th className="text-right p-4 font-medium text-slate-600">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {passwords.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="p-8 text-center text-slate-500">
                                    <Key className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Henüz şifre kaydı yok</p>
                                </td>
                            </tr>
                        ) : (
                            passwords.map((entry) => {
                                const DeviceIcon = getDeviceIcon(entry.device_type);
                                return (
                                    <tr key={entry.id} className="border-b hover:bg-slate-50">
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <DeviceIcon className="w-4 h-4 text-slate-400" />
                                                <span className="font-medium">{entry.device_name}</span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <Badge variant="outline">
                                                {getDeviceLabel(entry.device_type)}
                                            </Badge>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                                                    {entry.username}
                                                </code>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="h-6 w-6"
                                                    onClick={() => copyToClipboard(entry.username)}
                                                >
                                                    <Copy className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                                                    {showPasswords[entry.id] ? entry.password : '••••••••'}
                                                </code>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="h-6 w-6"
                                                    onClick={() => togglePassword(entry.id)}
                                                >
                                                    {showPasswords[entry.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="h-6 w-6"
                                                    onClick={() => copyToClipboard(entry.password)}
                                                >
                                                    <Copy className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            {entry.ip_address && (
                                                <code className="bg-slate-100 px-2 py-1 rounded text-sm mr-2">
                                                    {entry.ip_address}
                                                </code>
                                            )}
                                            {entry.url && (
                                                <a href={entry.url} target="_blank" rel="noopener noreferrer" 
                                                   className="text-blue-500 hover:underline text-sm">
                                                    {entry.url}
                                                </a>
                                            )}
                                            {!entry.ip_address && !entry.url && '-'}
                                        </td>
                                        <td className="p-4">
                                            {entry.location ? (
                                                <div className="flex items-center gap-1 text-slate-600">
                                                    <MapPin className="w-3 h-3" />
                                                    {entry.location}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    onClick={() => openEditDialog(entry)}
                                                    data-testid={`edit-password-${entry.id}`}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="text-red-500 hover:text-red-600"
                                                    onClick={() => handleDelete(entry)}
                                                    data-testid={`delete-password-${entry.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingEntry ? 'Şifre Düzenle' : 'Yeni Şifre Ekle'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="grid grid-cols-2 gap-4 py-4">
                        <div className="space-y-2">
                            <Label>Cihaz Adı *</Label>
                            <Input
                                value={formData.device_name}
                                onChange={(e) => setFormData({...formData, device_name: e.target.value})}
                                placeholder="Örn: Ana Router"
                                data-testid="password-device-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Cihaz Tipi *</Label>
                            <Select value={formData.device_type} onValueChange={(v) => setFormData({...formData, device_type: v})}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Tip seçin" />
                                </SelectTrigger>
                                <SelectContent>
                                    {deviceTypes.map(type => (
                                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Kullanıcı Adı *</Label>
                            <Input
                                value={formData.username}
                                onChange={(e) => setFormData({...formData, username: e.target.value})}
                                placeholder="Örn: admin"
                                data-testid="password-username-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Şifre *</Label>
                            <Input
                                value={formData.password}
                                onChange={(e) => setFormData({...formData, password: e.target.value})}
                                placeholder="••••••"
                                data-testid="password-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>IP Adresi</Label>
                            <Input
                                value={formData.ip_address}
                                onChange={(e) => setFormData({...formData, ip_address: e.target.value})}
                                placeholder="Örn: 192.168.1.1"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>URL</Label>
                            <Input
                                value={formData.url}
                                onChange={(e) => setFormData({...formData, url: e.target.value})}
                                placeholder="Örn: https://admin.example.com"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Konum</Label>
                            <Input
                                value={formData.location}
                                onChange={(e) => setFormData({...formData, location: e.target.value})}
                                placeholder="Örn: Server Odası"
                            />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label>Notlar</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                placeholder="Ek notlar..."
                                rows={2}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            İptal
                        </Button>
                        <Button 
                            onClick={handleSubmit}
                            className="bg-orwion-green hover:bg-orwion-green-hover"
                            data-testid="save-password-btn"
                        >
                            <Check className="w-4 h-4 mr-2" />
                            {editingEntry ? 'Güncelle' : 'Kaydet'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Passwords;
